import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { track, trackBeginCheckout } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Loader2, UploadCloud, Check, X, ArrowRight, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Options", "Personalize", "Consent", "Photos", "Pay"];

export default function Customize() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const inputRef = useRef();

  const [product, setProduct] = useState(null);
  const [step, setStep] = useState(0);
  const [variantId, setVariantId] = useState(location.state?.selection?.variant_id);
  const [upsells, setUpsells] = useState(location.state?.selection?.upsell_keys || []);
  const [busy, setBusy] = useState(false);

  const [pz, setPz] = useState({
    customer_name: user?.name || "", email: user?.email || "", recipient_name: "", age: "",
    theme: "", favorite_colors: "", interests: "", relationship: "", special_message: "",
    preferred_title: "", special_instructions: "",
  });
  const [discountCode, setDiscountCode] = useState("");
  const [photoConsent, setPhotoConsent] = useState(false);
  const [guardianConsent, setGuardianConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [orderId, setOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (!variantId) setVariantId(r.data.variants?.[0]?.id);
      track.personalizationStarted({ product_id: r.data.slug, product_type: r.data.category });
    }).catch(() => setProduct(false));
  }, [slug]); // eslint-disable-line

  if (product === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Product not found.</div>;
  if (!product) return <div className="max-w-3xl mx-auto px-4 py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;

  const requiresPhotos = product.requires_photos;
  const activeSteps = requiresPhotos ? STEPS : STEPS.filter((s) => s !== "Photos");
  const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
  const upsellObjs = (product.upsells || []).filter((u) => upsells.includes(u.key));
  const unitPrice = variant.price + upsellObjs.reduce((s, u) => s + u.price, 0);
  const toggleUpsell = (k) => setUpsells((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const next = () => setStep((s) => Math.min(s + 1, activeSteps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const createOrder = async () => {
    setBusy(true);
    try {
      const payload = {
        items: [{ product_slug: product.slug, variant_id: variant.id, upsell_keys: upsells, quantity: 1 }],
        personalization: pz,
        discount_code: discountCode || null,
        referral_code: localStorage.getItem("mfm_ref") || null,
        photo_consent: photoConsent,
        marketing_consent: marketingConsent,
        guardian_consent: guardianConsent,
        preview_lead_id: new URLSearchParams(location.search).get("preview") || null,
      };
      const { data } = await api.post("/orders", payload);
      setOrderId(data.id);
      setOrderNumber(data.order_number);
      track.personalizationCompleted({ product_id: product.slug, product_type: product.category, value: unitPrice });
      toast.success(`Order ${data.order_number} created`);
      next(); // to Photos or Pay
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const doUpload = async () => {
    if (files.length === 0) { next(); return; }
    setBusy(true);
    track.photoUploadStarted({ product_id: product.slug, quantity: files.length });
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/orders/${orderId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      setUploaded(data.uploaded);
      track.photoUploadCompleted({ product_id: product.slug, quantity: data.uploaded.length });
      const flagged = data.uploaded.filter((u) => u.quality === "replacement_recommended");
      if (flagged.length) {
        toast.warning(`${flagged.length} photo(s) may not produce the best illustration — faces hard to see. You can continue or replace them.`);
      }
      toast.success(`${data.uploaded.length} photo(s) uploaded`);
      next();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    trackBeginCheckout({ value: unitPrice, items: [{ slug: product.slug, name: product.name, category: product.category, price: unitPrice }] });
    try {
      const { data } = await api.post("/payments/checkout", { order_id: orderId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  const stepName = activeSteps[step];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      {/* Stepper */}
      <div className="flex items-center justify-between mb-8">
        {activeSteps.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className={`flex items-center gap-2 ${i <= step ? "text-amber" : "text-slate-400"}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < step ? "bg-sage text-white" : i === step ? "bg-amber text-white" : "bg-slate-100 text-slate-400"}`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </span>
              <span className="text-xs font-semibold hidden sm:inline">{s}</span>
            </div>
            {i < activeSteps.length - 1 && <div className={`h-0.5 flex-1 mx-2 ${i < step ? "bg-sage" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-8">
        <h1 className="font-display text-2xl font-bold text-slateink mb-1">{product.name}</h1>
        <p className="text-slate-500 text-sm mb-6">Total so far: <span className="font-semibold text-slateink">${unitPrice.toFixed(2)}</span></p>

        {stepName === "Options" && (
          <div className="space-y-5">
            <div>
              <Label className="font-semibold">Choose your option</Label>
              <div className="space-y-2 mt-2">
                {product.variants.map((v) => (
                  <button key={v.id} onClick={() => setVariantId(v.id)} data-testid={`cz-variant-${v.id}`}
                    className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 text-left ${variantId === v.id ? "border-amber bg-amber-50" : "border-slate-200"}`}>
                    <span className="font-medium">{v.label}</span><span className="font-semibold">${v.price}</span>
                  </button>
                ))}
              </div>
            </div>
            {product.upsells?.length > 0 && (
              <div>
                <Label className="font-semibold">Add extras</Label>
                <div className="space-y-2 mt-2">
                  {product.upsells.map((u) => (
                    <label key={u.key} className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-2.5 cursor-pointer">
                      <Checkbox checked={upsells.includes(u.key)} onCheckedChange={() => toggleUpsell(u.key)} />
                      <span className="flex-1 text-sm">{u.label}</span><span className="text-sm font-semibold text-slate-600">+${u.price}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {stepName === "Personalize" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" v={pz.customer_name} on={(x) => setPz({ ...pz, customer_name: x })} testid="pz-customer-name" />
            <Field label="Your email" v={pz.email} on={(x) => setPz({ ...pz, email: x })} testid="pz-email" type="email" />
            <Field label="Recipient's name" v={pz.recipient_name} on={(x) => setPz({ ...pz, recipient_name: x })} testid="pz-recipient" />
            <Field label="Age (if applicable)" v={pz.age} on={(x) => setPz({ ...pz, age: x })} testid="pz-age" />
            <Field label="Theme" v={pz.theme} on={(x) => setPz({ ...pz, theme: x })} testid="pz-theme" placeholder="e.g. Space adventure" />
            <Field label="Favorite colors" v={pz.favorite_colors} on={(x) => setPz({ ...pz, favorite_colors: x })} testid="pz-colors" />
            <Field label="Interests" v={pz.interests} on={(x) => setPz({ ...pz, interests: x })} testid="pz-interests" />
            <Field label="Relationship" v={pz.relationship} on={(x) => setPz({ ...pz, relationship: x })} testid="pz-relationship" placeholder="e.g. Grandma & grandson" />
            <Field label="Preferred title" v={pz.preferred_title} on={(x) => setPz({ ...pz, preferred_title: x })} testid="pz-title" />
            <div className="sm:col-span-2">
              <Label>Special message / dedication</Label>
              <Textarea value={pz.special_message} onChange={(e) => setPz({ ...pz, special_message: e.target.value })} data-testid="pz-message" className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label>Special instructions</Label>
              <Textarea value={pz.special_instructions} onChange={(e) => setPz({ ...pz, special_instructions: e.target.value })} data-testid="pz-instructions" className="mt-1" />
            </div>
          </div>
        )}

        {stepName === "Consent" && (
          <div className="space-y-5">
            <div>
              <Label>Have a discount or referral code?</Label>
              <Input value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} data-testid="cz-discount" placeholder="e.g. WELCOME10" className="mt-1 uppercase" />
            </div>
            <label className="flex items-start gap-3 bg-subtle rounded-xl p-4 cursor-pointer">
              <Checkbox checked={photoConsent} onCheckedChange={setPhotoConsent} data-testid="cz-photo-consent" className="mt-0.5" />
              <span className="text-sm text-slate-700">I understand that my uploaded images will only be used to create my purchased product. <span className="text-rose">*</span></span>
            </label>
            <label className="flex items-start gap-3 bg-subtle rounded-xl p-4 cursor-pointer">
              <Checkbox checked={guardianConsent} onCheckedChange={setGuardianConsent} data-testid="cz-guardian-consent" className="mt-0.5" />
              <span className="text-sm text-slate-700">I confirm that I am the parent, legal guardian, or otherwise have permission to submit these photographs and create this personalized product. <span className="text-rose">*</span></span>
            </label>
            <label className="flex items-start gap-3 rounded-xl p-4 border border-slate-200 cursor-pointer">
              <Checkbox checked={marketingConsent} onCheckedChange={setMarketingConsent} data-testid="cz-marketing-consent" className="mt-0.5" />
              <span className="text-sm text-slate-700">I give Made For Me Studio permission to display my finished product or submitted images for marketing purposes. <span className="text-slate-400">(optional)</span></span>
            </label>
            <div className="bg-sage/10 rounded-xl p-4 text-sm text-slate-600 flex items-start gap-2">
              <Lock className="w-4 h-4 text-sage mt-0.5 shrink-0" /> Your photos are stored in secure private storage and auto-deleted after your order completes.
            </div>
          </div>
        )}

        {stepName === "Photos" && (
          <div>
            <p className="text-sm text-slate-600 mb-1">Upload {product.min_photos}–{product.max_photos} photos for order <span className="font-semibold">{orderNumber}</span>.</p>
            <p className="text-xs text-amber-700/80 bg-amber-50 rounded-lg px-3 py-2 mb-4">For best results, upload clear, well-lit photos where faces are visible.</p>
            <button type="button" onClick={() => inputRef.current?.click()} data-testid="photo-upload-input"
              className="w-full border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-amber transition-colors">
              <UploadCloud className="w-8 h-8 text-amber mx-auto" />
              <p className="text-sm text-slate-600 mt-2 font-medium">Tap to choose photos</p>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG or HEIC — up to 15MB each</p>
            </button>
            <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif" className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            {files.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
                {files.map((f, i) => (
                  <div key={i} className="relative rounded-xl overflow-hidden aspect-square bg-subtle">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1"><X className="w-3 h-3 text-slate-700" /></button>
                  </div>
                ))}
              </div>
            )}
            {busy && uploadPct > 0 && <Progress value={uploadPct} className="mt-4" />}
          </div>
        )}

        {stepName === "Pay" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-sage/10 text-sage flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8" /></div>
            <h2 className="font-display text-xl font-bold text-slateink">You're all set!</h2>
            <p className="text-slate-600 mt-2">Order <span className="font-semibold">{orderNumber}</span> is ready for secure payment.</p>
            <div className="bg-subtle rounded-xl p-4 mt-5 text-left max-w-xs mx-auto">
              <div className="flex justify-between text-sm py-1"><span className="text-slate-500">{variant.label}</span><span className="font-medium">${variant.price.toFixed(2)}</span></div>
              {upsellObjs.map((u) => <div key={u.key} className="flex justify-between text-sm py-1"><span className="text-slate-500">{u.label}</span><span className="font-medium">+${u.price.toFixed(2)}</span></div>)}
              <div className="flex justify-between font-semibold pt-2 mt-1 border-t border-slate-200"><span>Est. total</span><span>${unitPrice.toFixed(2)}</span></div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Discounts are applied at checkout.</p>
          </div>
        )}

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <Button variant="ghost" onClick={back} disabled={step === 0 || busy} className="text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {stepName === "Options" && <Button onClick={next} data-testid="cz-next" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {stepName === "Personalize" && <Button onClick={next} data-testid="cz-next" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {stepName === "Consent" && <Button onClick={createOrder} disabled={busy || !photoConsent || !guardianConsent} data-testid="cz-create-order" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}</Button>}
          {stepName === "Photos" && <Button onClick={doUpload} disabled={busy} data-testid="cz-upload" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : files.length ? "Upload & continue" : "Skip for now"}</Button>}
          {stepName === "Pay" && <Button onClick={pay} disabled={busy} data-testid="cz-pay" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold"><Lock className="w-4 h-4 mr-1" /> {busy ? "Redirecting…" : "Pay securely"}</Button>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, testid, type = "text", placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={testid} placeholder={placeholder} className="mt-1" />
    </div>
  );
}
